import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import InvokeLambda from '../lib/lambdaInvokeRegistrationData'
import RegistrationForm from '../components/Auth/Registration/RegistrationForm'
import Card from 'antd/lib/card'
import Spin from 'antd/lib/spin'
import Button from 'antd/lib/button'
import Badge from 'antd/lib/badge'
import loginBackground from '../assets/images/login.png'
import BSSLogo from '../components/Certificate/BSSLogo'
import { setData, setLoading, setErrorMessage, setShowLogin, setShowHelp, setShowNotifications } from '../redux/auth/actions'
import Login from '../components/Auth/Login'
import Help from '../components/Auth/Help'
import EmailExist from '../components/Auth/Registration/EmailExist'
import InvalidCode from '../components/Auth/Registration/InvalideCode'
import Notifications from '../components/Auth/Notifications'
import NotificationIcon from '../icons/Notifications'
import QuestionIcon from '../icons/Question'
import NewPassword from '../components/Auth/NewPassword'

const styles = {
    card: {
        background: 'rgba(255, 255, 255, 0.74)',
        borderRadius: '12px',
        padding: '0 2% 0 2%',
    },
    formContainer: {
        margin: '1.64em auto',
        width: 480,
    },
    logoContainer: {
        margin: '10px auto',
        textAlign: 'center',
        width: 200,
    },
    link: {
        color: '#1BAAF1',
        cursor: 'pointer',
    },
    loadingContainer: {
        marginTop: '1em',
        display: 'flex',
        justifyContent: 'center',
    },
    loading: {
        color: '#1BAAF1',
    },
    infoContainer: {
        margin: '1em 0 0.32em 0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        textAlign: 'center'
    },
    error: {
        color: 'red',
        textAlign: 'center',
        fontSize: 14,
    },
    footer: {
        margin: '0.64em auto',
        display: 'flex',
        justifyContent: 'space-around',
        width: 90
    },
    buttonSwitcher: {
        background: '#fff',
        margin: 'auto',
        borderRadius: 12,
        width: 200
    },
    button: {
        cursor: 'pointer',
        background: '#fff',
        border: 'none',
        width: 100,
        height: 40,
        borderRadius: 12,
        color: 'rgb(24, 144, 255)'
    }
}

const Registration = ({ authState, authData }) => {
    const showLogin = useSelector((state) => state.auth.showLogin)
    const isLoading = useSelector(state => state.auth.loading)
    const width = useSelector((state) => state.app.width)
    const data = useSelector((state) => state.auth.data)
    const userAttributes = useSelector((state) => state.auth.userAttributes)
    const errorMessage = useSelector((state) => state.auth.errorMessage)
    const showConfirmation = useSelector(state => state.auth.showConfirmationForm)
    const showNewPassword = useSelector((state) => state.auth.showNewPassword)

    const [showLinkError, setShowLinkError] = useState(false)

    useEffect(() => {
        setErrorMessage('')
        document.body.style.backgroundImage = `url(${loginBackground})`
        document.body.style.backgroundPosition = 'left center'
        document.body.style.backgroundRepeat = 'no-repeat'
        document.body.style.backgroundSize = 'cover'
        document.body.style.backgroundColor = '#bdccd4'
        return () => {
            document.body.style.backgroundColor = null
        }
    }, [])

    useEffect(() => {
        if (authData?.token) {
            validateToken(authData.token)
        }
    }, [authData])

    const validateToken = (token) => {
        const tokenPattern = /^[a-zA-Z0-9]{8}$/i
        if (token.length === 8 && tokenPattern.test(token)) {
            loadTenantInformation(token)
        } else {
            setShowLinkError(true)
        }
    }

    const loadTenantInformation = (token) => {
        InvokeLambda(token).then((response) => {
            if (response.tenant) {
                setData(response)
                setErrorMessage('')
            } else {
                setShowLinkError(true)
            }
            setLoading(false)
        })
    }

    const containerStyle = width < 576 ? { ...styles.formContainer, width: '86%' } : styles.formContainer

    const onButtonClick = () => {
        window.location.replace('/')
    }

    if (authState !== 'signUp') return null

    return (
        <>
            <LogoSection />
            <FormContainer
                containerStyle={containerStyle}
                data={data}
                showConfirmation={showConfirmation}
                isLoading={isLoading}
                errorMessage={errorMessage}
                showLogin={showLogin}
                userAttributes={userAttributes}
                onButtonClick={onButtonClick}
                showLinkError={showLinkError}
                showNewPassword={showNewPassword}
            />
            <Help />
            <Notifications />
            <EmailExist />
        </>
    )
}

const LogoSection = () => (
    <div style={styles.logoContainer}>
        <svg
            version="1.1"
            viewBox="0 0 126.17 53.72"
            width="100%"
            xmlns="http://www.w3.org/2000/svg"
        >
            <BSSLogo />
        </svg>
    </div>
)

const FormContainer = ({
    containerStyle,
    data,
    showConfirmation,
    isLoading,
    errorMessage,
    showLogin,
    userAttributes,
    onButtonClick,
    showLinkError,
    showNewPassword
}) => {

    const renderContent = () => {
        if (showLinkError) {
            return <InvalidCode />
        } else if (showConfirmation) {
            return <ConfirmationSection onButtonClick={onButtonClick} />
        } else if (showNewPassword) {
            return <NewPassword />
        } else if (data && !isLoading) {
            return (
                <MainFormSection
                    showLogin={showLogin}
                    errorMessage={errorMessage}
                    data={data}
                    userAttributes={userAttributes}
                />
            )
        } else {
            return <LoadingSection />
        }
    }

    const cardStyle = showConfirmation ? {...styles.card, marginTop: 180} : styles.card

    return (
        <div style={containerStyle}>
            {data && !showConfirmation && (
                <div style={styles.infoContainer}>
                    <b>{data.building.name}</b>
                    <span style={{ color: '#606060' }}>{data.tenant.name}</span>
                </div>
            )}
            <Card style={cardStyle}>
                {renderContent()}
            </Card>
            {showLogin && (
                <FooterSection />
            )}
        </div>
    );
};

const ConfirmationSection = ({ onButtonClick }) => (
    <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
        <span style={{ fontSize: 20, fontWeight: 600, marginBottom: '1.32em' }}>Registration Successful</span>
        <span>You have been sent a confirmation email from <b>system@bssnet.com</b></span>
        <div style={{ marginTop: '0.64em' }}>
            <Button style={{ marginTop: '1em' }} type='primary' onClick={onButtonClick}>Continue To Login</Button>
        </div>
    </div>
)

const MainFormSection = ({ showLogin, data, userAttributes }) => {

    return (
        <>
            <div style={styles.buttonSwitcher}>
                <button onClick={() => setShowLogin(true)} style={showLogin ? { ...styles.button, background: 'rgb(24, 144, 255)', color: '#fff' } : styles.button}>Login</button>
                <button onClick={() => setShowLogin(false)} style={!showLogin ? { ...styles.button, background: 'rgb(24, 144, 255)', color: '#fff' } : styles.button}>Register</button>
            </div>
            {!showLogin ? <RegistrationForm data={data} attributes={userAttributes} /> : <Login />}
        </>
    )
}

const LoadingSection = () => (
    <div style={styles.loadingContainer}>
        <Spin style={styles.loading} tip="Loading..." />
    </div>
)

const FooterSection = () => (
    <div style={styles.footer}>
        <span style={{ cursor: 'pointer' }} onClick={() => setShowHelp(true)}>
            <QuestionIcon height={32} />
        </span>
        <span style={{ cursor: 'pointer' }} onClick={() => setShowNotifications(true)}>
            <Badge>
                <NotificationIcon height={32} />
            </Badge>
        </span>

    </div>
)

export default Registration
